import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Flag } from "lucide-react";
import { STAGE_ACCENT, STAGES } from "@/lib/stages";

// "Semua" + each of the 6 pipeline stages individually — lets the calendar be filtered down
// to just e.g. SPH or just Lost, instead of always showing every stage mixed together.
const STAGE_FILTERS = ["Semua", ...STAGES.map((s) => s.id)];

const DOW = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isoToKey(iso) {
  if (!iso) return null;
  return (iso.length >= 10) ? iso.slice(0, 10) : null;
}

export default function PipelineCalendar({ leads, onSelectLead }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [stageFilter, setStageFilter] = useState("Semua");

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = first.getDay();
    const start = new Date(first);
    start.setDate(first.getDate() - startOffset);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [cursor]);

  const byDate = useMemo(() => {
    const map = {};
    const source = stageFilter === "Semua" ? leads : leads.filter((l) => l.status === stageFilter);
    for (const l of source) {
      // No manual toggle needed — the sensible date for a lead's stage is picked
      // automatically: Closing shows on its closing date, everything else shows on its
      // follow-up date (the "next thing happens" date for that stage either way).
      const key = isoToKey(l.status === "Closing" ? (l.closing_date || l.created_at) : l.follow_up_date);
      if (!key) continue;
      (map[key] = map[key] || []).push(l);
    }
    return map;
  }, [leads, stageFilter]);

  const todayKey = dateKey(today);
  const monthLabel = `${MONTHS_ID[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const goto = (delta) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));

  return (
    <div className="px-3 md:px-8">
      <div className="card-surface rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-3.5 border-b border-white/5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => goto(-1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"><ChevronLeft size={16} /></button>
            <div className="flex items-center gap-1.5 font-semibold text-sm min-w-[130px] justify-center"><CalendarDays size={15} className="text-blue-400" />{monthLabel}</div>
            <button data-testid="cal-next" onClick={() => goto(1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"><ChevronRight size={16} /></button>
            <button data-testid="cal-today" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="text-xs px-2.5 py-1.5 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10">Hari ini</button>
          </div>
        </div>

        {/* Which stage(s) to show — defaults to Semua so nothing looks hidden/broken on
            first load. Date placement is automatic now (see byDate above), no toggle needed. */}
        <div className="flex items-center gap-1.5 flex-wrap px-3.5 py-2.5 border-b border-white/5">
          {STAGE_FILTERS.map((s) => (
            <button key={s} data-testid={`cal-stage-${s}`} onClick={() => setStageFilter(s)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${stageFilter === s ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-[#18181B] text-zinc-500 border-white/5"}`}>
              {s}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-7 border-b border-white/5">
          {DOW.map((d) => (
            <div key={d} className="text-center text-[11px] font-medium text-zinc-500 py-2">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const key = dateKey(d);
            const items = byDate[key] || [];
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = key === todayKey;
            return (
              <div key={i} data-testid={`cal-day-${key}`}
                className={`min-h-[92px] sm:min-h-[110px] border-r border-b border-white/5 p-1.5 last:border-r-0 ${inMonth ? "" : "opacity-30"}`}>
                <div className={`text-[11px] w-5 h-5 flex items-center justify-center rounded-full mb-1 ${isToday ? "bg-blue-600 text-white font-bold" : "text-zinc-500"}`}>
                  {d.getDate()}
                </div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((l) => (
                    <button key={l.lead_id} onClick={() => onSelectLead(l.lead_id)}
                      className="w-full text-left text-[10px] px-1.5 py-1 rounded-md leading-tight truncate hover:opacity-80 transition-opacity"
                      style={{ background: (STAGE_ACCENT[l.status] || "#71717a") + "22", color: STAGE_ACCENT[l.status] || "#a1a1aa", borderLeft: `2px solid ${STAGE_ACCENT[l.status] || "#71717a"}` }}>
                      {l.nama}
                    </button>
                  ))}
                  {items.length > 3 && (
                    <div className="text-[9.5px] text-zinc-500 px-1.5">+{items.length - 3} lagi</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mt-3 px-1">
        {Object.entries(STAGE_ACCENT).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5 text-[11px] text-zinc-400">
            <span className="w-2 h-2 rounded-full" style={{ background: c }} />{k}
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 ml-auto">
          <Flag size={11} />Closing berdasarkan tanggal closing, tahap lain berdasarkan tanggal follow-up
        </div>
      </div>
    </div>
  );
}
